import React, { useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';

const MetricInner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Class names from metrics_strip_component.html.slim -- kept in sync manually, there's no shared
// constant between Ruby and TS for these.
const STRIP_ITEM_CLASS = 'js-loswp-work-card-download-count';
const STRIP_CLASS = 'js-loswp-work-card-metrics-strip';
// Any OTHER strip item (e.g. the server-rendered citations item) besides this one's own wrapper.
const OTHER_STRIP_ITEM_SELECTOR =
  '.ds-work-card--metrics-strip-item:not(.js-loswp-work-card-download-count)';

type Props = {
  workId: number;
  // Minimum count required to render — low download counts are anti-social-proof.
  // Defaults to 0 (always render once loaded) to preserve the ungated surface's behavior.
  minCount?: number;
};

export function UngatedWorkCardDownloadCount({ workId, minCount = 0 }: Props) {
  const [count, setCount] = useState<number | null>(null);
  // Anchor for locating our position in the DOM even when we render nothing visible -- the
  // server always emits our wrapper .js-loswp-work-card-download-count and the outer
  // .js-loswp-work-card-metrics-strip (see the "collapse" effect below), so this never renders
  // null on its own; low/absent counts still leave those wrappers in the tree with their
  // flex/margin styles applied, which is exactly the empty-strip gap this collapses.
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let cancelled = false;
    const url = routes.api_v0_work_download_count_path({
      work_id: workId,
      subdomain_param: 'api',
    });

    fetchWithCsrf(url)
      .then((res) => (res.ok ? res.json() : undefined))
      .then((data: { count?: number } | undefined) => {
        if (!cancelled && data && typeof data.count === 'number') {
          setCount(data.count);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [workId]);

  // Once the count resolves below minCount (never rises again for this page load), collapse the
  // wrappers left behind instead of leaving an empty, styled flex row. Only fires on a resolved
  // low count, not while still loading (count === null) -- collapsing during the fetch would
  // flash the strip closed and then reopen it for a count that turns out to qualify.
  useEffect(() => {
    if (count === null || count >= minCount) return;

    const item = anchorRef.current?.closest(`.${STRIP_ITEM_CLASS}`);
    item?.classList.add('ds-work-card--metrics-strip-item--empty');

    const strip = anchorRef.current?.closest(`.${STRIP_CLASS}`);
    if (strip && !strip.querySelector(OTHER_STRIP_ITEM_SELECTOR)) {
      strip.classList.add('ds-work-card--metrics-strip--empty');
    }
  }, [count, minCount]);

  if (count === null || count < minCount) {
    return <span ref={anchorRef} style={{ display: 'none' }} />;
  }

  return (
    <MetricInner>
      <Icon
        name="download"
        size={16}
        className="ungated-work-card--metric-icon"
      />
      <Body size="md" className="ungated-work-card--metric-number">
        {count.toLocaleString()}
      </Body>
      <Body size="xs" className="ungated-work-card--metric-label">
        {count === 1 ? 'download' : 'downloads'}
      </Body>
    </MetricInner>
  );
}
